import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  List,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { CountryCode } from 'libphonenumber-js'
import { uniqBy, uniqueId } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'urql'
import { useDebounce } from 'use-debounce'

import { GET_ORGANIZATIONS } from '@app/components/OrgSelector/queries/get-organizations'
import {
  isDfeSuggestion,
  isHubOrg,
  isXeroSuggestion,
  OrgSelectorProps,
  useOrganizationToBeCreatedOnRegistration,
} from '@app/components/OrgSelector/UK/utils'
import { useAuth } from '@app/context/auth'
import {
  GetOrganizationsQuery,
  GetOrganizationsQueryVariables,
  InsertOrgLeadMutation,
  Organization,
  Dfe_Establishment,
  FindEstablishmentQuery,
  FindEstablishmentQueryVariables,
} from '@app/generated/graphql'
import { organizationData as localStateOrganization } from '@app/util'

import { AddOrg as UKAddOrg } from '../components/UK/AddOrg'
import { FIND_ESTABLISHMENTS } from '../queries/find-establishment'

type OptionToAdd = Dfe_Establishment | { id?: string; name: string }
type Option = Organization | OptionToAdd

const getOptionLabel = (option: Option) => option.name ?? ''
export const OrgSelector: React.FC<React.PropsWithChildren<OrgSelectorProps>> =
  function ({
    value,
    showTrainerOrgOnly = false,
    onChange,
    onInputChange,
    sx,
    textFieldProps,
    placeholder,
    autocompleteMode = false,
    allowAdding = false,
    showHubResults = true,
    showDfeResults = true,
    error,
    disabled = false,
    required = false,
    isShallowRetrieval = false,
    isEditProfile,
    userOrgIds,
    showUserOrgsOnly = false,
    countryCode,
    searchOnlyByPostCode = false,
    label,
    createdFrom,
    disableOrganisationEnquiryOrCreation = false,
    showOnlyMainOrgs = false,
    showOnlyPossibleAffiliatedOrgs = false,
    mainOrgId,
    ...props
  }) {
    const { t } = useTranslation()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const { profile } = useAuth()
    const [open, setOpen] = useState(false)
    const [adding, setAdding] = useState<OptionToAdd | null>()
    const [q, setQ] = useState('')
    const [debouncedQuery] = useDebounce(q, 300)
    const localSavedOrgToBeCreated = useOrganizationToBeCreatedOnRegistration()

    useEffect(() => {
      if (q) setOpen(true)
    }, [q])

    const orgIds =
      userOrgIds ?? profile?.organizations.map(org => org.organization.id)

    const myOrg = useMemo(
      () => profile?.organizations.map(org => org.organization),
      [profile?.organizations],
    )
    const userOrgFilteredOrgs = useMemo(
      () =>
        orgIds ? (myOrg ?? []).filter(org => orgIds.includes(org.id)) : myOrg,
      [myOrg, orgIds],
    )
    const showTrainerNonAOLOrgs: boolean = showTrainerOrgOnly && !!profile
    const defaultOrg = useMemo(
      () =>
        showTrainerOrgOnly && profile?.organizations.length === 1
          ? profile?.organizations[0]?.organization
          : undefined,
      [profile?.organizations, showTrainerOrgOnly],
    )

    useEffect(() => {
      if (!value) {
        setQ('')
      }
    }, [value])

    const onChangeRef = React.useRef(onChange)
    useEffect(() => {
      onChangeRef.current = onChange
    })

    useEffect(() => {
      if (showTrainerNonAOLOrgs && defaultOrg) {
        onChangeRef.current(defaultOrg)
      }
    }, [showTrainerNonAOLOrgs, defaultOrg])

    const [{ data: dfeOrgs, fetching: dfeFetching }, refetchDfeEstablishment] =
      useQuery<FindEstablishmentQuery, FindEstablishmentQueryVariables>({
        query: FIND_ESTABLISHMENTS,
        variables: {
          searchTerm: debouncedQuery || '',
          where: localStateOrganization?.dfeId
            ? {
                id: { _neq: localStateOrganization.dfeId },
              }
            : undefined,
        },
        pause: !debouncedQuery,
      })

    const [{ data: hubOrgs, fetching: orgsFetching }, refetchOrganisations] =
      useQuery<GetOrganizationsQuery, GetOrganizationsQueryVariables>({
        query: GET_ORGANIZATIONS,
        variables: {
          isNotShallow: !isShallowRetrieval,
          isShallow: isShallowRetrieval,
          searchTerm: debouncedQuery || '',
          where: {
            _and: [
              showOnlyMainOrgs
                ? { main_organisation_id: { _is_null: true } }
                : {},
              showOnlyPossibleAffiliatedOrgs
                ? {
                    main_organisation_id: { _is_null: true },
                    affiliated_organisations_aggregate: {
                      count: { predicate: { _eq: 0 } },
                    },
                  }
                : {},
              mainOrgId ? { id: { _neq: mainOrgId } } : {},
            ],
          },
        },
        pause: !debouncedQuery,
      })

    const refetchAllOrganisations = useCallback(() => {
      refetchDfeEstablishment({ requestPolicy: 'network-only' })
      refetchOrganisations({ requestPolicy: 'network-only' })
    }, [refetchDfeEstablishment, refetchOrganisations])

    const handleClose = () => setAdding(null)
    const handleSuccess = async (org: InsertOrgLeadMutation['org']) => {
      if (!org) return
      setAdding(null)

      onChange(org)

      refetchAllOrganisations()
    }
    const handleChange = (
      event: React.SyntheticEvent,
      option: Option | null,
    ) => {
      event.preventDefault()
      if (option === null) {
        onChange(null)
        return
      }
      if (!autocompleteMode && (!isHubOrg(option) || isDfeSuggestion(option))) {
        return setTimeout(() => setAdding(option))
      }
      if (autocompleteMode && isXeroSuggestion(option)) {
        onChange({
          name: option.name,
          xeroId:
            'contactID' in option ? (option.contactID as string) : undefined,
        })
      } else {
        onChange(option)
      }
    }

    const options = useMemo(() => {
      if (showUserOrgsOnly && !!profile) {
        return userOrgFilteredOrgs as Option[]
      }
      if (showTrainerNonAOLOrgs) {
        return myOrg as Option[]
      }
      if (!debouncedQuery || orgsFetching || dfeFetching) {
        return []
      }

      return uniqBy(
        [
          ...(localSavedOrgToBeCreated?.name
            .toLowerCase()
            .includes(debouncedQuery.toLowerCase())
            ? [{ ...localSavedOrgToBeCreated, id: uniqueId() }]
            : []),
          ...(showHubResults ? hubOrgs?.orgs ?? [] : []),
          ...(showDfeResults ? dfeOrgs?.establishments ?? [] : []),
          ...(!disableOrganisationEnquiryOrCreation &&
          allowAdding &&
          !autocompleteMode &&
          debouncedQuery
            ? [{ name: debouncedQuery }]
            : []),
          ...(!disableOrganisationEnquiryOrCreation && !allowAdding
            ? [{ name: t('components.org-selector.request-creation-sub') }]
            : []),
        ],
        'id',
      )
    }, [
      showUserOrgsOnly,
      userOrgFilteredOrgs,
      profile,
      showTrainerNonAOLOrgs,
      debouncedQuery,
      orgsFetching,
      dfeFetching,
      localSavedOrgToBeCreated,
      showHubResults,
      hubOrgs?.orgs,
      disableOrganisationEnquiryOrCreation,
      allowAdding,
      autocompleteMode,
      t,
      myOrg,
      showDfeResults,
      dfeOrgs,
    ])

    const noOptionsText = useMemo(() => {
      let text = ''

      if (q) {
        if (dfeFetching || orgsFetching) text = t('loading')
        else text = t('components.org-selector.no-results')
      } else {
        text = t('components.org-selector.start-typing')
      }

      return <Typography variant="body2">{text}</Typography>
    }, [dfeFetching, orgsFetching, q, t])

    function renderAddress(option: Option) {
      if (!isHubOrg(option) && !isDfeSuggestion(option)) return ''
      const address = isDfeSuggestion(option)
        ? {
            line1: option.addressLineOne,
            line2: option.addressLineTwo,
            city: option.town,
            postCode: option.postcode,
            region: null,
          }
        : option.address ?? {}
      return [
        address.line1,
        address.line2,
        address.city,
        address.postCode,
        address.region,
      ]
        .filter(Boolean)
        .join(', ')
    }
    const organizationCreationOrEnquiryButton = useCallback(
      (option: Option): string => {
        if (isDfeSuggestion(option)) {
          return t('add')
        }
        return t(allowAdding ? 'create' : 'organisation-enquiry')
      },
      [allowAdding, t],
    )

    return (
      <>
        <Autocomplete
          defaultValue={defaultOrg}
          value={!value ? null : value}
          open={open}
          onOpen={() => {
            if (
              (showTrainerNonAOLOrgs && myOrg?.length) ||
              localSavedOrgToBeCreated
            ) {
              setOpen(true)
            }
          }}
          onClose={() => setOpen(false)}
          data-testid="org-selector"
          sx={sx}
          openOnFocus
          clearOnBlur={false}
          inputValue={value ? getOptionLabel(value) : q}
          onChange={handleChange}
          onInputChange={(_, newValue, reason) => {
            if (reason !== 'reset') {
              setQ(newValue)
              onInputChange?.(newValue)
            }
          }}
          options={options}
          filterOptions={
            !showTrainerOrgOnly
              ? options =>
                  isEditProfile
                    ? options.filter(option =>
                        !('id' in option) ? true : !orgIds?.includes(option.id),
                      )
                    : options
              : undefined
          }
          getOptionLabel={getOptionLabel}
          noOptionsText={noOptionsText}
          isOptionEqualToValue={(o, v) => {
            if (!('id' in v) || !('id' in o)) return false
            return o.id === v.id
          }}
          loading={dfeFetching || orgsFetching || Boolean(q && !debouncedQuery)}
          disabled={disabled}
          groupBy={(value: Option) => {
            if (isDfeSuggestion(value)) {
              return t('components.org-selector.dfe-suggestions')
            }
            if (isXeroSuggestion(value)) {
              return t('components.org-selector.tt-suggestions')
            }
            if (isHubOrg(value)) {
              return orgIds?.includes(value.id)
                ? t('components.org-selector.my-organizations')
                : t('components.org-selector.existing-organizations')
            } else {
              return t(
                allowAdding
                  ? 'components.org-selector.add-manually'
                  : 'components.org-selector.request-creation',
              )
            }
          }}
          renderGroup={params => (
            <li
              key={params.key}
              data-testid={`org-selector-result-group-${params.group}`}
            >
              <Grid container justifyContent="space-between" p={2} py={1}>
                <Typography
                  display="inline"
                  variant="body2"
                  fontWeight={isMobile ? 700 : 300}
                >
                  {params.group}
                </Typography>
              </Grid>
              <List sx={{ p: 0, justifyContent: 'space-between' }}>
                {params.children}
              </List>
            </li>
          )}
          renderInput={params => (
            <TextField
              {...textFieldProps}
              {...params}
              data-testid="org-name"
              label={
                label ??
                (searchOnlyByPostCode
                  ? t('components.org-selector.residing-org')
                  : t('components.org-selector.title'))
              }
              InputLabelProps={{
                shrink: true,
                required,
              }}
              placeholder={
                placeholder ??
                (searchOnlyByPostCode
                  ? t('components.org-selector.post-code-placeholder')
                  : t('components.org-selector.placeholder'))
              }
              InputProps={{
                ...params.InputProps,

                endAdornment: (
                  <React.Fragment>
                    {dfeFetching || orgsFetching ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {autocompleteMode ? null : params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              error={!!error}
              helperText={error || ''}
            />
          )}
          renderOption={(props, option) => {
            const isNew =
              !isHubOrg(option) ||
              isXeroSuggestion(option) ||
              isDfeSuggestion(option)
            const address = renderAddress(option)
            const key = !('id' in option) ? 'NEW_ORG' : (option?.id as string)

            return (
              <Box
                display="flex"
                flexDirection={isMobile ? 'column' : 'row'}
                justifyContent="space-between"
                px={2}
                component="li"
                {...props}
                {...(isNew && !autocompleteMode
                  ? {
                      onClick: undefined,
                      onTouchStart: undefined,
                    }
                  : {})}
                key={key}
                data-testid={`org-selector-result-${key}`}
              >
                <Typography
                  flex={1}
                  p={1}
                  fontStyle={isNew ? 'italic' : undefined}
                  component="span"
                  display="flex"
                  flexDirection={isMobile ? 'column' : 'row'}
                  alignItems={isMobile ? 'left' : 'center'}
                >
                  {getOptionLabel(option)}
                  <Typography variant="body2" color="grey.700" ml={1}>
                    {address}
                  </Typography>
                </Typography>
                {isNew && !autocompleteMode ? (
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => {
                      if (!isDfeSuggestion(option) && !allowAdding) {
                        window.location.href =
                          import.meta.env.VITE_ORGANISATION_ENQUIRY
                        return
                      }
                      setAdding(option)
                    }}
                    size="small"
                    fullWidth={isMobile}
                    data-testid="new-organisation"
                  >
                    {organizationCreationOrEnquiryButton(option)}
                  </Button>
                ) : null}
              </Box>
            )
          }}
          {...props}
        />
        {adding ? (
          <UKAddOrg
            option={adding}
            countryCode={(countryCode as CountryCode) ?? 'GB-ENG'}
            onClose={handleClose}
            onSuccess={handleSuccess}
            createdFrom={createdFrom}
          />
        ) : null}
      </>
    )
  }
